import { DynamicRepeatingQuestion } from "./questionTypes";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Trash2, Plus } from "lucide-react";
import { TooltipHelper } from "./TooltipHelper";

/**
 * Safely converts any value to a format acceptable by HTML input elements
 * Handles boolean conversion and null/undefined gracefully
 */
const toInputValue = (value: unknown): string | number | readonly string[] => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value.toString();
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  return String(value);
};

interface DynamicRepeatingQuestionProps {
  question: DynamicRepeatingQuestion;
  values: Array<Record<string, string | number | boolean>>;
  onChange: (questionId: string, values: Array<Record<string, string | number | boolean>>) => void;
}

export function DynamicRepeatingQuestionComponent({
  question,
  values,
  onChange
}: DynamicRepeatingQuestionProps) {
  const handleFieldChange = (itemIndex: number, fieldId: string, value: string | number | boolean) => {
    const newValues = [...values];
    if (!newValues[itemIndex]) {
      newValues[itemIndex] = {};
    }
    newValues[itemIndex][fieldId] = value;
    onChange(question.id, newValues);
  };

  const addItem = () => {
    if (values.length < question.maxItems) {
      const newValues = [...values, {}];
      onChange(question.id, newValues);
    }
  };

  const removeItem = (index: number) => {
    if (values.length > question.minItems) {
      const newValues = values.filter((_, i) => i !== index);
      onChange(question.id, newValues);
    }
  };

  // Calculate percentage total if validation rules exist
  const validationRule = question.validationRules?.type === 'sum' 
    ? question.validationRules 
    : null;
  
  let currentSum = 0;
  let showValidationError = false;
  
  if (validationRule) {
    currentSum = values.reduce((total, item) => {
      const value = item[validationRule.field];
      return total + (typeof value === 'number' ? value : parseFloat(String(value)) || 0);
    }, 0);
    showValidationError = Math.abs(currentSum - validationRule.targetValue) > 0.01 && values.length > 0;
  }

  // Ensure we have at least minItems
  const itemsToShow = Math.max(values.length, question.minItems);
  const displayValues = [...values];
  while (displayValues.length < itemsToShow) {
    displayValues.push({});
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-start gap-2 mb-2">
          <h2 className="text-2xl font-bold text-foreground">{question.title}</h2>
          {question.tooltip && (
            <TooltipHelper content={question.tooltip} className="mt-1" />
          )}
        </div>
        {question.description && (
          <p className="text-muted-foreground mb-6">{question.description}</p>
        )}
      </div>

      {/* Validation summary */}
      {validationRule && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground">
            Total: {currentSum.toFixed(2)}{validationRule.field === 'percentage' ? '%' : ''} of {validationRule.targetValue}{validationRule.field === 'percentage' ? '%' : ''}
          </div>
          {showValidationError && (
            <div className="text-sm text-destructive">
              {validationRule.errorMessage.replace('{currentSum}', currentSum.toFixed(2))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {displayValues.map((item, index) => (
          <Card key={index} className="relative">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  {question.itemLabel} {index + 1}
                </h3>
                {values.length > question.minItems && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {question.fields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </label>
                    
                    {field.type === 'select' && field.options ? (
                      <select
                        value={toInputValue(item[field.id])}
                        onChange={(e) => handleFieldChange(index, field.id, e.target.value)}
                        className="w-full p-3 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required={field.required}
                      >
                        <option value="">{field.placeholder || `Select ${field.label}`}</option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'slider' ? (
                      <div className="space-y-2">
                        <input
                          type="range"
                          min={field.minValue || 0}
                          max={field.maxValue || 100}
                          step={field.step || 1}
                          value={toInputValue(item[field.id])}
                          onChange={(e) => handleFieldChange(index, field.id, parseInt(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>{field.minValue || 0}{field.valueLabel || ''}</span>
                          <span className="font-medium text-foreground">
                            {item[field.id] || 0}{field.valueLabel || ''}
                          </span>
                          <span>{field.maxValue || 100}{field.valueLabel || ''}</span>
                        </div>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={toInputValue(item[field.id])}
                        onChange={(e) => handleFieldChange(index, field.id, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full p-3 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required={field.required}
                      />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
            
            {index < displayValues.length - 1 && (
              <Separator className="mt-4" />
            )}
          </Card>
        ))}
      </div>

      {/* Add button */}
      {values.length < question.maxItems && (
        <Button
          type="button"
          variant="outline"
          onClick={addItem}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          {question.addButtonText}
        </Button>
      )}
    </div>
  );
}